import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";

import { Slot } from "@radix-ui/react-slot";

import { SkeletonAwait } from "~/components/suspense/skeleton";
import { useURL } from "~/contexts";
import type { VariantData, VariantPriceInfo } from "~/hec/types/variant-data";
import type {
  C_ProductInfo,
  ImageGroup,
  Product,
  ProductSwatch,
  VariationAttribute,
  VariationAttributeValue,
  VariationGroup,
} from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { invariant } from "~/lib/utils/utils";
import { getSwatches } from "~/shop/utils/swatches";
import { getSelectedVariant } from "~/shop/utils/variants";

const VariantContext = createContext<{
  productId: string;
  master?: Promise<Product | null> | Product | null;
  product: {
    variationAttributes?: Array<VariationAttribute>;
    variationGroups?: Array<VariationGroup>;
  };
  selected: { [key: string]: string };
  setSelected: (type: string, value: string) => void;
  swatches?: ProductSwatch[];
  /** Deferred per-variant data (inventory + imageGroups + `c_priceInfo`) keyed
   * by variant productId. Filled by the PDP loader's fan-out. Consumers that
   * need to react after it resolves (e.g. ProductImages, ProductBuyForm, the
   * `SelectedVariant` price) read `resolvedVariantData` instead. */
  variantDataMap?: Promise<Record<string, VariantData>>;
  /** Resolved snapshot of `variantDataMap`, or `null` until it settles.
   * Reactive — consumers re-render when it lands. */
  resolvedVariantData: Record<string, VariantData> | null;
}>(undefined as any);

// eslint-disable-next-line react-refresh/only-export-components
export const useVariantContext = () => {
  const context = useContext(VariantContext);
  if (!context) {
    throw new Error(
      "VariantContext must be used within a VariantContextProvider",
    );
  }
  return context;
};

/** Optional variant context — returns `null` when called outside a
 * `<VariantSelector>` boundary. Used by components that should gracefully
 * degrade when no Selector wraps them (e.g. standalone `<ProductImages>`
 * usages). */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalVariantContext = () =>
  useContext(VariantContext) ?? null;

export const VariantSelector = ({
  product,
  master,
  productId,
  children,
  onSelect,
  onChange,
  defaultValue,
  variantDataMap,
}: {
  productId: string;
  master?: Promise<Product | null> | Product | null;
  onChange?: (value: { [key: string]: string }) => void;
  defaultValue?: { [key: string]: string };
  product: SerializeFrom<C_ProductInfo> & {
    variationAttributes?: Array<VariationAttribute>;
    variationGroups?: Array<VariationGroup>;
    c_imageGroups?: ImageGroup[];
  };
  onSelect?: (type: string, value: string) => void;
  variantDataMap?: Promise<Record<string, VariantData>>;
  children: ReactNode;
}) => {
  const mounted = useRef<boolean>(false);
  const [selected, setSelectedState] = useState<{ [key: string]: string }>(
    defaultValue ?? {},
  );

  useEffect(() => {
    if (onChange && mounted.current) onChange(selected);
  }, [selected]);

  /* This must be after the above hook so mounted is set to true after it has run */
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const setSelected = useCallback(
    (type: string, value: string) => {
      setSelectedState(prev => ({ ...prev, [type]: value }));
      onSelect?.(type, value);
    },
    [onSelect],
  );
  const swatches = useMemo(() => getSwatches(product), [product]);

  // Resolve the deferred per-variant data once and expose it via context so
  // consumers (ProductImages, ProductBuyForm availability copy) re-render
  // when it lands.
  const [resolvedVariantData, setResolvedVariantData] = useState<Record<
    string,
    VariantData
  > | null>(null);
  useEffect(() => {
    if (!variantDataMap) return;
    let cancelled = false;
    variantDataMap.then(v => {
      if (!cancelled) setResolvedVariantData(v);
    });
    return () => {
      cancelled = true;
    };
  }, [variantDataMap]);

  return (
    <VariantContext.Provider
      value={{
        product,
        productId,
        selected,
        setSelected,
        swatches,
        master,
        variantDataMap,
        resolvedVariantData,
      }}
    >
      {children}
    </VariantContext.Provider>
  );
};

const VariantOptionContext = createContext<{
  type: string;
  options: VariationAttributeValue[];
  selectedValue: string;
  setSelected: (value: string) => void;
}>(undefined as any);

const useVariantOptionContext = () => {
  const context = useContext(VariantOptionContext);
  if (!context) {
    throw new Error(
      "VariantOptionContext must be used within a VariantOptionContextProvider",
    );
  }
  return context;
};

export const VariantOptionSelect = ({
  type,
  children,
}: {
  type: string;
  children: (props: {
    attribute: VariationAttribute;
    selected: string;
    allSelected: { [key: string]: string };
  }) => ReactNode;
}) => {
  const { product, selected, setSelected } = useVariantContext();
  const selectedValue = selected[type];
  const [attribute, options] = useMemo(
    () => [
      product.variationAttributes?.find(attr => attr.id === type),
      product.variationAttributes?.find(attr => attr.id === type)?.values ?? [],
    ],
    [product, type, selected],
  );

  if (!attribute) {
    return null;
  }
  return (
    <VariantOptionContext.Provider
      value={{
        type,
        options,
        selectedValue,
        setSelected: (value: string) => setSelected(type, value),
      }}
    >
      {options.length > 0
        ? children({
            attribute,
            selected: selectedValue,
            allSelected: selected,
          })
        : null}
    </VariantOptionContext.Provider>
  );
};

export const VariantOptionValues = ({
  children,
  asChild,
}: {
  asChild?: boolean;
  children: (props: {
    url: string;
    option: VariationAttributeValue;
    selectedValue: string;
    setSelected: (value: string) => void;
    swatch?: ProductSwatch;
    variationGroup: VariationGroup | undefined;
  }) => ReactNode;
}) => {
  const context = useVariantOptionContext();
  const { selected, productId, swatches, product } = useVariantContext();

  const getUrl = useURL();
  const [params] = useSearchParams();
  const { type } = context;
  const getSwatch = useCallback(
    (option: VariationAttributeValue) =>
      swatches?.find(s => s.color.value === option.value),
    [swatches, type],
  );

  const url = useCallback(
    (value: string) => {
      const searchParams = new URLSearchParams({
        ...Object.fromEntries(params.entries()),
        ...selected,
        [context.type]: value,
      });
      return getUrl(
        productId +
          `.html${
            // searchParams.size is null in Safari in older then  v17 versions
            // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/size
            searchParams.keys().next().value ? "?" : ""
          }` +
          searchParams.toString(),
      );
    },
    [selected, params, getUrl, productId, context.type],
  );

  return context.options.map((option, i) => {
    const groupSelection = { ...selected, [type]: option.value };
    const variationGroup = product.variationGroups?.find(group =>
      Object.entries(group.variationValues).every(
        ([key, value]) => groupSelection[key] === value,
      ),
    );
    return asChild ? (
      <Slot
        key={`${option.value}-${i}`}
        onClick={() => context.setSelected(option.value)}
      >
        {children({
          url: url(option.value),
          option,
          selectedValue: context.selectedValue,
          setSelected: context.setSelected,
          swatch: getSwatch(option),
          variationGroup,
        })}
      </Slot>
    ) : (
      children({
        url: url(option.value),
        option,
        selectedValue: context.selectedValue,
        setSelected: context.setSelected,
        swatch: getSwatch(option),
        variationGroup,
      })
    );
  });
};

export const VariantSelectedVariant = ({
  children,
}: {
  children: ({
    salePrice,
    currentPrice,
    variant,
  }:
    | {
        salePrice: number | undefined;
        currentPrice: number | undefined;
        variant?: { productId: string; orderable?: boolean | undefined };
        selected: { [key: string]: string };
        allAttributesSelected: boolean;
        /** SELECTED variant's `c_priceInfo` for the price override
         * (SFCC-11575): a value drives it, `null` skips it (switched variant
         * whose fan-out hasn't resolved), `undefined` = no variant context. */
        selectedPriceInfo?: VariantPriceInfo | null;
      }
    | {
        salePrice?: undefined;
        currentPrice?: undefined;
        variant?: undefined;
        selected: { [key: string]: string };
        allAttributesSelected: boolean;
        selectedPriceInfo?: VariantPriceInfo | null;
      }) => React.ReactNode;
}) => {
  const { master, selected, product, resolvedVariantData } =
    useVariantContext();
  // `master` resolves to `null` for simple (non-variant) products — a valid
  // state now that the loader resolves master INLINE on document requests
  // (deferDataRequest). The SkeletonAwait + render-prop below are null-safe
  // (`master?.`), so a null master renders the master-less title/price fine.
  // Only guard against master genuinely missing from context; `useVariantContext`
  // above already throws when there's no provider. (A bare `invariant(master)`
  // here threw on every simple-product document/SSR render — a 500 on hard load.)
  invariant(
    master !== undefined,
    "Cannot use SelectedVariant without a master in context",
  );
  return (
    <SkeletonAwait
      resolve={master}
      skeleton={children({ selected, allAttributesSelected: false })}
    >
      {master => {
        const allAttributes = master?.variationAttributes?.length;
        const variationGroup = master?.variationGroups?.find(variant => {
          return Object.entries(selected).every(
            ([key, value]) => variant.variationValues[key] === value,
          );
        });
        const variant = getSelectedVariant(master, selected);
        const currentVariantId = variationGroup?.productId;
        // Show the selected variant's actual price. Masters no longer bump to
        // priceMax or synthesize a strikethrough from the variant spread — the
        // PLP tile owns the "$min - $max" range; the PDP shows the chosen
        // variant, with a strikethrough only when that variant is genuinely on
        // sale (a real per-variant `c_saleprice`).
        const currentPrice = variant?.price || master?.price;
        const masterSalePrice = currentVariantId
          ? master?.c_saleprice?.[currentVariantId]
          : null;

        let salePrice =
          currentVariantId &&
          masterSalePrice &&
          typeof masterSalePrice === "number"
            ? masterSalePrice
            : undefined;

        if (
          masterSalePrice &&
          typeof masterSalePrice === "object" &&
          masterSalePrice.salesprice
        ) {
          salePrice = masterSalePrice.salesprice;
        }

        // SELECTED variant's `c_priceInfo` for the price override (SFCC-11575).
        // Key off `variant` (from `master.variants`), NOT `variationGroup`
        // (empty without variation groups). Prefer the per-variant fan-out; else
        // the landed product's own when the selection IS the landed variant
        // (SSR-immediate); else `null` (selected but fan-out unresolved → skip
        // override). `undefined` only with no variant (standalone/bundle → legacy).
        const selectedVariantId = variant?.productId;
        const landed = product as {
          id?: string;
          c_priceInfo?: VariantPriceInfo;
        };
        const selectedPriceInfo: VariantPriceInfo | null | undefined =
          selectedVariantId
            ? (resolvedVariantData?.[selectedVariantId]?.priceInfo ??
              (selectedVariantId === landed.id ? landed.c_priceInfo : null) ??
              null)
            : undefined;

        return children({
          salePrice,
          currentPrice,
          variant,
          selected,
          allAttributesSelected: allAttributes === Object.keys(selected).length,
          selectedPriceInfo,
        });
      }}
    </SkeletonAwait>
  );
};
